import React,{useEffect,useMemo,useState} from "react";
import {toast} from "react-toastify";
import axiosInstance from "../axiosInstance";
import "./ExpenditureWorkspace.css";

const today=()=>new Date().toISOString().slice(0,10);
const monthStart=()=>`${today().slice(0,7)}-01`;
const money=v=>Number(v||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2});

export default function ExpenditureWorkspace(){
 const [context,setContext]=useState({categories:[],payment_methods:[],branches:[]});
 const [tab,setTab]=useState("daily");
 const [branchId,setBranchId]=useState("");
 const [date,setDate]=useState(today());
 const [daily,setDaily]=useState({});
 const [expenses,setExpenses]=useState([]);
 const [report,setReport]=useState(null);
 const [search,setSearch]=useState("");
 const [sourceFilter,setSourceFilter]=useState("ALL");
 const [registerFrom,setRegisterFrom]=useState(monthStart());
 const [registerTo,setRegisterTo]=useState(today());
 const [loadingExpenses,setLoadingExpenses]=useState(false);
 const [busy,setBusy]=useState(false);
 const [showExpense,setShowExpense]=useState(false);
 const [form,setForm]=useState({expense_date:today(),category_id:"",description:"",payee:"",amount:"",payment_method_id:"",reference:"",branch_id:""});
 const now=new Date();
 const [year,setYear]=useState(now.getFullYear());
 const [month,setMonth]=useState(now.getMonth()+1);

 const currency=context.currency||"";

 const loadContext=async()=>{
  const r=await axiosInstance.get("expenditure/context/");
  setContext(r.data||{});
  const branches=r.data?.branches||[];
  if(!branchId&&branches.length===1)setBranchId(String(branches[0].id));
 };

 const loadDaily=async()=>{
  const r=await axiosInstance.get("expenditure/daily-reconciliation/",{params:{date,...(branchId?{branch_id:branchId}:{})}});
  setDaily(r.data||{});
 };

 const loadExpenses=async()=>{
  if(!registerFrom||!registerTo){toast.error("Select both the From and To dates.");return}
  if(registerTo<registerFrom){toast.error("The To date cannot be before the From date.");return}
  setLoadingExpenses(true);
  try{
   const r=await axiosInstance.get("expenditure/",{params:{date_from:registerFrom,date_to:registerTo,source:sourceFilter,...(branchId?{branch_id:branchId}:{}),...(search.trim()?{search:search.trim()}:{})}});
   setExpenses(r.data?.expenses||[]);
  }finally{setLoadingExpenses(false)}
 };

 const loadReport=async()=>{
  const r=await axiosInstance.get("expenditure/month-end/",{params:{year,month,...(branchId?{branch_id:branchId}:{})}});
  setReport(r.data||null);
 };

 useEffect(()=>{loadContext().catch(()=>toast.error("Could not load expenditure setup."))},[]);
 useEffect(()=>{loadDaily().catch(()=>{});loadReport().catch(()=>{})},[branchId,date,year,month]);
 useEffect(()=>{loadExpenses().catch(()=>toast.error("Could not load expenditure records."))},[branchId,registerFrom,registerTo,sourceFilter]);

 const saveDaily=async e=>{
  e.preventDefault(); setBusy(true);
  try{
   const r=await axiosInstance.post("expenditure/daily-reconciliation/",{
    date,
    branch_id:branchId,
    opening_balance:daily.opening_balance||0,
    collected:daily.collected||0,
    notes:daily.notes||"",
   });
   setDaily(r.data.summary||{});
   toast.success(r.data.detail);
   await loadReport();
  }catch(err){toast.error(err?.response?.data?.detail||"Daily reconciliation could not be saved.")}
  finally{setBusy(false)}
 };

 const openExpense=()=>{
  setForm({expense_date:date,category_id:context.categories?.[0]?.id||"",description:"",payee:"",amount:"",payment_method_id:"",reference:"",branch_id:branchId||context.branches?.[0]?.id||""});
  setShowExpense(true);
 };

 const saveExpense=async e=>{
  e.preventDefault(); setBusy(true);
  try{
   const r=await axiosInstance.post("expenditure/",form);
   toast.success(r.data.detail);
   setShowExpense(false);
   await Promise.all([loadDaily(),loadExpenses(),loadReport()]);
  }catch(err){toast.error(err?.response?.data?.detail||"Expenditure could not be recorded.")}
  finally{setBusy(false)}
 };

 const reverse=async item=>{
  const reason=window.prompt(`Reason for reversing ${item.code}:`);
  if(!reason)return;
  try{
   const r=await axiosInstance.post(`expenditure/${item.id}/reverse/`,{reason});
   toast.success(r.data.detail);
   await Promise.all([loadDaily(),loadExpenses(),loadReport()]);
  }catch(err){toast.error(err?.response?.data?.detail||"Could not reverse expenditure.")}
 };

 const exportCsv=()=>{
  if(!report)return;
  const rows=[
   ["MONTH END EXPENDITURE REPORT",`${year}-${String(month).padStart(2,"0")}`],
   ["Currency",report.currency],
   ["Opening balance",report.summary.opening_balance],
   ["Collected",report.summary.collected],
   ["Receipted",report.summary.receipted],
   ["Unreceipted difference",report.summary.unreceipted_difference],
   ["Spent",report.summary.spent],
   ["Remaining",report.summary.remaining],
   [],
   ["EXPENDITURE SOURCES","Amount"],
   ...(report.by_source||[]).map(x=>[x.source,x.amount]),
   [],
   ["WHERE MONEY WAS CHANNELLED","Amount"],
   ...(report.by_category||[]).map(x=>[x.category,x.amount]),
   [],
   ["Date","Collected","Receipted","Spent","Net"],
   ...(report.daily||[]).map(x=>[x.date,x.collected,x.receipted,x.spent,x.net]),
  ];
  const csv=rows.map(r=>r.map(v=>`"${String(v??"").replaceAll('"','""')}"`).join(",")).join("\n");
  const blob=new Blob([csv],{type:"text/csv"});
  const url=URL.createObjectURL(blob);
  const a=document.createElement("a"); a.href=url; a.download=`EXPENDITURE_${year}_${String(month).padStart(2,"0")}.csv`; a.click();
  URL.revokeObjectURL(url);
 };

 const maxCategory=useMemo(()=>Math.max(1,...(report?.by_category||[]).map(x=>Number(x.amount||0))),[report]);
 const registerTotal=useMemo(()=>expenses.reduce((total,item)=>total+Number(item.base_amount??item.amount??0),0),[expenses]);
 const registerSources=useMemo(()=>expenses.reduce((totals,item)=>{
  const source=String(item.source_type||"MANUAL").toUpperCase();
  totals[source]=(totals[source]||0)+1;
  return totals;
 },{}),[expenses]);
 const sourceLabel={ALL:"All expenditure",MANUAL:"Manual expenses",CLAIM:"Settled claims",COMMISSION:"Paid commissions",ECOSURE:"EcoSure payments"}[sourceFilter]||sourceFilter;

 return <section className="expenditure-page">
  <header className="expenditure-head">
   <div><span>FINANCE CONTROL</span><h1>Cash & Expenditure</h1><p>One register for manual costs and verified cash movements created by claims, commissions and EcoSure.</p></div>
   <div className="expenditure-actions">
    {context.branches?.length>0&&<select value={branchId} onChange={e=>setBranchId(e.target.value)}><option value="">ALL BRANCHES</option>{context.branches.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}</select>}
    {context.can_manage&&<button onClick={openExpense}><i className="bi bi-plus-lg"/>Record expenditure</button>}
   </div>
  </header>

  <nav className="expenditure-tabs">
   <button className={tab==="daily"?"active":""} onClick={()=>setTab("daily")}>Daily position</button>
   <button className={tab==="register"?"active":""} onClick={()=>setTab("register")}>Expenditure register</button>
   <button className={tab==="month"?"active":""} onClick={()=>setTab("month")}>Month-end report</button>
  </nav>

  <div className="system-expense-note"><i className="bi bi-shield-check"/><div><strong>Automatic system expenditure is read from its original transaction.</strong><span>Do not capture settled claims, effected commission payments or EcoSure remittances again as manual expenses.</span></div></div>

  {tab==="daily"&&<>
   <div className="expenditure-date"><label>Working date<input type="date" value={date} onChange={e=>setDate(e.target.value)}/></label></div>
   <div className="cash-kpis">
    <article><span>Collected</span><strong>{currency} {money(daily.collected)}</strong><small>Money recorded after collections</small></article>
    <article><span>Receipted</span><strong>{currency} {money(daily.receipted)}</strong><small>{daily.receipt_count||0} receipts posted</small></article>
    <article><span>Used / spent</span><strong>{currency} {money(daily.spent)}</strong><small>{daily.manual_expense_count||0} manual · {daily.system_expense_count||0} automatic</small></article>
    <article><span>Remaining</span><strong>{currency} {money(daily.remaining)}</strong><small>Opening + collections − expenditure</small></article>
   </div>
   <div className="daily-grid">
    <form className="daily-form" onSubmit={saveDaily}>
     <header><div><span>DAILY RECONCILIATION</span><h3>Collection control</h3></div></header>
     <label>Opening balance<input type="number" min="0" step="0.01" value={daily.opening_balance||"0.00"} onChange={e=>setDaily({...daily,opening_balance:e.target.value})}/></label>
     <label>Money collected / found<input type="number" min="0" step="0.01" value={daily.collected||"0.00"} onChange={e=>setDaily({...daily,collected:e.target.value})}/></label>
     <label>System receipted<input value={`${currency} ${money(daily.receipted)}`} readOnly/></label>
     <label>Difference<input value={`${currency} ${money(daily.unreceipted_difference)}`} readOnly/></label>
     <label className="wide">Notes<textarea value={daily.notes||""} onChange={e=>setDaily({...daily,notes:e.target.value})}/></label>
     {context.can_manage&&<button disabled={busy}>{busy?"Saving...":"Save daily reconciliation"}</button>}
    </form>
    <section className="daily-explain">
     <span>CONTROL FORMULA</span><h3>Where today's money stands</h3>
     <div><b>Opening balance</b><strong>{currency} {money(daily.opening_balance)}</strong></div>
     <div><b>+ Collections</b><strong>{currency} {money(daily.collected)}</strong></div>
     <div><b>− Expenditure</b><strong>{currency} {money(daily.spent)}</strong></div>
     <div className="expense-source-line"><b>Manual entries</b><strong>{currency} {money(daily.manual_spent)}</strong></div>
     <div className="expense-source-line"><b>Automatic system entries</b><strong>{currency} {money(daily.system_spent)}</strong></div>
     <div className="total"><b>= Funds remaining</b><strong>{currency} {money(daily.remaining)}</strong></div>
     <p>Receipted money is read directly from NetriAssure receipts. Collections can be confirmed separately so shortages or unreceipted differences are visible.</p>
    </section>
   </div>
  </>}

  {tab==="register"&&<>
   <section className="register-filter-panel">
    <header><div><span>EXPENDITURE REPORT PERIOD</span><h3>Choose what you want to view</h3></div><p>Claims use their settlement date. EcoSure and commissions use their actual payment date.</p></header>
    <div className="register-toolbar">
     <label><span>From</span><input type="date" value={registerFrom} onChange={e=>setRegisterFrom(e.target.value)}/></label>
     <label><span>To</span><input type="date" value={registerTo} onChange={e=>setRegisterTo(e.target.value)}/></label>
     <label><span>Expense source</span><select value={sourceFilter} onChange={e=>setSourceFilter(e.target.value)}><option value="ALL">ALL EXPENDITURE</option><option value="CLAIM">SETTLED CLAIMS</option><option value="ECOSURE">ECOSURE PAYMENTS</option><option value="COMMISSION">PAID COMMISSIONS</option><option value="MANUAL">MANUAL EXPENSES</option></select></label>
     <label className="register-search"><span>Search within results</span><input placeholder="Expense number, policy, payee or reference" value={search} onChange={e=>setSearch(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")loadExpenses().catch(()=>toast.error("Could not load expenditure records."))}}/></label>
     <button disabled={loadingExpenses} onClick={()=>loadExpenses().catch(()=>toast.error("Could not load expenditure records."))}>{loadingExpenses?"Loading...":"Apply filters"}</button>
    </div>
   </section>
   <div className="register-summary">
    <article><span>Viewing</span><strong>{sourceLabel}</strong><small>{registerFrom} to {registerTo}</small></article>
    <article><span>Records found</span><strong>{expenses.length}</strong><small>{sourceFilter==="ALL"?Object.entries(registerSources).map(([key,value])=>`${key}: ${value}`).join(" · "):"Matching records in this period"}</small></article>
    <article><span>Period expenditure</span><strong>{currency} {money(registerTotal)}</strong><small>Converted to the company base currency</small></article>
   </div>
   <div className="expense-table"><table><thead><tr><th>Date</th><th>Source</th><th>Expense</th><th>Category</th><th>Payee</th><th>Description</th><th>Method</th><th>Reference</th><th>Amount</th><th>Status</th><th/></tr></thead><tbody>
    {expenses.map(x=><tr key={x.row_key||`${x.source_type}-${x.id}`} className={x.is_system?"system-row":""}><td>{x.expense_date}</td><td><span className={`expense-source source-${String(x.source_type||"manual").toLowerCase()}`}>{x.source_type||"MANUAL"}</span></td><td>{x.code}</td><td>{x.category}</td><td>{x.payee||"—"}</td><td>{x.description}</td><td>{x.payment_method||"—"}</td><td>{x.reference||"—"}</td><td><strong>{x.currency_code||currency} {money(x.amount)}</strong>{x.currency_code&&x.currency_code!==currency&&<small>Base: {currency} {money(x.base_amount)}</small>}</td><td>{x.status}</td><td>{context.can_manage&&x.can_reverse&&<button onClick={()=>reverse(x)}>Reverse</button>}{x.is_system&&<span className="source-locked" title="Manage this entry in its original module"><i className="bi bi-lock"/> Source controlled</span>}</td></tr>)}
    {!expenses.length&&<tr><td colSpan="11" className="empty">No {sourceLabel.toLowerCase()} found between {registerFrom} and {registerTo}.</td></tr>}
   </tbody></table></div>
  </>}

  {tab==="month"&&<>
   <div className="month-toolbar"><select value={month} onChange={e=>setMonth(Number(e.target.value))}>{Array.from({length:12},(_,i)=><option value={i+1} key={i+1}>{new Date(2000,i,1).toLocaleString(undefined,{month:"long"})}</option>)}</select><input type="number" value={year} onChange={e=>setYear(Number(e.target.value))}/><button onClick={loadReport}>Refresh</button><button onClick={exportCsv}>Export CSV</button></div>
   {report&&<>
    <div className="cash-kpis month-kpis"><article><span>Collected</span><strong>{currency} {money(report.summary.collected)}</strong></article><article><span>Receipted</span><strong>{currency} {money(report.summary.receipted)}</strong></article><article><span>Total expenditure</span><strong>{currency} {money(report.summary.spent)}</strong></article><article><span>Funds remaining</span><strong>{currency} {money(report.summary.remaining)}</strong></article></div>
    <div className="month-grid">
     <section className="channel-panel"><header><span>WHERE MONEY WAS CHANNELLED</span><h3>Expenditure by category</h3></header>{(report.by_category||[]).map(x=><div className="channel-row" key={x.category}><div><b>{x.category}</b><strong>{currency} {money(x.amount)}</strong></div><i style={{width:`${Math.max(2,Number(x.amount||0)/maxCategory*100)}%`}}/></div>)}{!report.by_category?.length&&<p>No expenditure recorded for this month.</p>}</section>
     <section className="channel-panel"><header><span>PAYMENT CHANNELS</span><h3>How expenditure was paid</h3></header>{(report.by_payment_method||[]).map(x=><div className="method-row" key={x.payment_method}><span>{x.payment_method}</span><strong>{currency} {money(x.amount)}</strong></div>)}</section>
     <section className="channel-panel source-panel"><header><span>AUDITABLE SOURCES</span><h3>Manual versus automatic</h3></header>{(report.by_source||[]).map(x=><div className="method-row" key={x.source}><span>{x.source}</span><strong>{currency} {money(x.amount)}</strong></div>)}</section>
    </div>
    <div className="expense-table daily-report"><table><thead><tr><th>Date</th><th>Collected</th><th>Receipted</th><th>Spent</th><th>Net movement</th></tr></thead><tbody>{(report.daily||[]).map(x=><tr key={x.date}><td>{x.date}</td><td>{money(x.collected)}</td><td>{money(x.receipted)}</td><td>{money(x.spent)}</td><td>{money(x.net)}</td></tr>)}</tbody></table></div>
   </>}
  </>}

  {showExpense&&<div className="expense-modal"><form onSubmit={saveExpense}><header><div><span>NEW EXPENDITURE</span><h2>Record money used</h2></div><button type="button" onClick={()=>setShowExpense(false)}>×</button></header><div className="expense-form">
   <label>Date<input type="date" required value={form.expense_date} onChange={e=>setForm({...form,expense_date:e.target.value})}/></label>
   <label>Branch<select required value={form.branch_id} onChange={e=>setForm({...form,branch_id:e.target.value})}><option value="">SELECT</option>{context.branches.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}</select></label>
   <label>Category<select required value={form.category_id} onChange={e=>setForm({...form,category_id:e.target.value})}><option value="">SELECT</option>{context.categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
   <label>Amount<input required type="number" min="0.01" step="0.01" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})}/></label>
   <label>Payee<input value={form.payee} onChange={e=>setForm({...form,payee:e.target.value.toUpperCase()})}/></label>
   <label>Payment method<select value={form.payment_method_id} onChange={e=>setForm({...form,payment_method_id:e.target.value})}><option value="">NOT SPECIFIED</option>{context.payment_methods.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}</select></label>
   <label>Reference<input value={form.reference} onChange={e=>setForm({...form,reference:e.target.value.toUpperCase()})}/></label>
   <label className="wide">Description<textarea required value={form.description} onChange={e=>setForm({...form,description:e.target.value.toUpperCase()})}/></label>
  </div><footer><button type="button" onClick={()=>setShowExpense(false)}>Cancel</button><button className="primary" disabled={busy}>{busy?"Saving...":"Record expenditure"}</button></footer></form></div>}
 </section>;
}
